import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { FiZap, FiCpu, FiTrendingUp, FiCheck, FiX, FiRefreshCw, FiChevronDown, FiChevronUp, FiActivity, FiTarget, FiAward, FiDownload, FiCopy, FiImage, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';

/**
 * AILearningInsights - Shows what the AI has learned from user approvals/rejections
 * 
 * This component visualizes:
 * - Learning stats (approvals, rejections, approval rate)
 * - Pattern insights (what hooks/visuals work)
 * - 70/30 system status (exploration vs pattern-guided)
 * - JIT Skill status
 */
function AILearningInsights({ clientId, clientName }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getClientLearningInsights(clientId);
      setInsights(data);
    } catch (err) {
      console.error('Failed to load learning insights:', err);
      setError('Could not load AI learning data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-muted rounded-lg"></div>
          <div className="h-6 bg-muted rounded w-48"></div>
        </div>
        <div className="h-4 bg-muted rounded w-full mb-2"></div>
        <div className="h-4 bg-muted rounded w-3/4"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
              <FiCpu className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">AI Learning</h3>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
          <button 
            onClick={loadInsights}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    );
  }

  if (!insights) return null;

  const { stats, learning_system, pattern_insights, avoidance_insights, jit_skill, recent_approvals, implicit_signals, top_performers } = insights;
  const isColdStart = learning_system?.status === 'cold_start';
  const hasImplicitSignals = (implicit_signals?.total_positive_signals || 0) + (implicit_signals?.total_negative_signals || 0) > 0;

  return (
    <div className="bg-gradient-to-br from-card to-card/80 border border-border rounded-xl overflow-hidden">
      {/* Header - Always visible */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-muted/30 transition-colors"
        data-testid="ai-learning-toggle"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-accent/20 rounded-xl flex items-center justify-center">
            <FiCpu className="w-6 h-6 text-primary" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              AI Learning
              {!isColdStart && (
                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-500 text-xs rounded-full">
                  Active
                </span>
              )}
              {isColdStart && (
                <span className="px-2 py-0.5 bg-amber-500/10 text-amber-500 text-xs rounded-full">
                  Cold Start
                </span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground">
              {isColdStart 
                ? 'Start using content to train the AI on your preferences'
                : hasImplicitSignals
                  ? `Learning from ${implicit_signals?.total_positive_signals || 0} positive signals • ${implicit_signals?.downloads?.total || 0} downloads • ${implicit_signals?.copies?.total || 0} copies`
                  : `Learning from ${stats?.approved_count || 0} approvals • ${learning_system?.exploration_rate} exploration`
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Quick stats */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm" title="Downloads (positive signal)">
              <FiDownload className="w-4 h-4 text-emerald-500" />
              <span className="text-foreground font-medium">{implicit_signals?.downloads?.total || 0}</span>
            </div>
            <div className="flex items-center gap-2 text-sm" title="Text copies (positive signal)">
              <FiCopy className="w-4 h-4 text-blue-500" />
              <span className="text-foreground font-medium">{implicit_signals?.copies?.total || 0}</span>
            </div>
            <div className="flex items-center gap-2 text-sm" title="Explicit approvals">
              <FiCheck className="w-4 h-4 text-emerald-500" />
              <span className="text-foreground font-medium">{stats?.approved_count || 0}</span>
            </div>
          </div>
          
          {expanded ? (
            <FiChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-6 pb-6 space-y-6 border-t border-border animate-fade-in">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <FiActivity className="w-4 h-4" />
                Total Generated
              </div>
              <div className="text-2xl font-bold text-foreground">{stats?.total_generated || 0}</div>
            </div>
            <div className="bg-emerald-500/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-emerald-600 text-sm mb-1">
                <FiCheck className="w-4 h-4" />
                Approved
              </div>
              <div className="text-2xl font-bold text-emerald-600">{stats?.approved_count || 0}</div>
            </div>
            <div className="bg-red-500/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-500 text-sm mb-1">
                <FiX className="w-4 h-4" />
                Rejected
              </div>
              <div className="text-2xl font-bold text-red-500">{stats?.rejected_count || 0}</div>
            </div>
            <div className="bg-primary/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-primary text-sm mb-1">
                <FiTrendingUp className="w-4 h-4" />
                Approval Rate
              </div>
              <div className="text-2xl font-bold text-primary">{stats?.approval_rate || 0}%</div>
            </div>
          </div>

          {/* 70/30 Learning System Explanation */}
          <div className="bg-muted/20 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
              <FiTarget className="w-4 h-4 text-primary" />
              70/30 Learning System
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Exploration (Fresh Ideas)</span>
                  <span className="text-sm font-medium text-foreground">{learning_system?.exploration_rate || '70%'}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                    style={{ width: learning_system?.exploration_rate || '70%' }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Novel angles, surprising content</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Pattern-Guided</span>
                  <span className="text-sm font-medium text-foreground">{learning_system?.pattern_rate || '30%'}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                    style={{ width: learning_system?.pattern_rate || '30%' }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Built on your approved content</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">
              <span className="text-amber-500">⚡</span> Next JIT Skill refinement in: <span className="font-medium text-foreground">{learning_system?.next_refinement_at || 5} approvals</span>
            </div>
          </div>

          {/* Implicit Signals - What FirstCut Learned from Your Actions */}
          {hasImplicitSignals && (
            <div className="bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-lg p-4 border border-blue-500/10">
              <h4 className="font-medium text-foreground mb-4 flex items-center gap-2">
                <FiActivity className="w-4 h-4 text-blue-500" />
                What FirstCut Learned (From Your Actions)
              </h4>
              
              {/* Signal Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 text-center">
                  <FiDownload className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                  <div className="text-lg font-bold text-foreground">{implicit_signals?.downloads?.total || 0}</div>
                  <div className="text-xs text-muted-foreground">Downloads</div>
                </div>
                <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 text-center">
                  <FiCopy className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <div className="text-lg font-bold text-foreground">{implicit_signals?.copies?.total || 0}</div>
                  <div className="text-xs text-muted-foreground">Text Copies</div>
                </div>
                <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 text-center">
                  <FiThumbsUp className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
                  <div className="text-lg font-bold text-foreground">{implicit_signals?.total_positive_signals || 0}</div>
                  <div className="text-xs text-muted-foreground">Positive Signals</div>
                </div>
                <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 text-center">
                  <FiRefreshCw className="w-5 h-5 text-amber-500 mx-auto mb-1" />
                  <div className="text-lg font-bold text-foreground">{implicit_signals?.regenerations?.total || 0}</div>
                  <div className="text-xs text-muted-foreground">Image Regens</div>
                </div>
              </div>

              {/* Top Performing Content */}
              {top_performers?.most_downloaded?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                    <FiDownload className="w-3 h-3" /> Most Downloaded Content
                  </div>
                  <div className="space-y-2">
                    {top_performers.most_downloaded.map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-emerald-500/5 rounded p-2 text-sm">
                        <span className="text-foreground truncate flex-1">&ldquo;{item.hook}&rdquo;</span>
                        <span className="flex items-center gap-1 text-emerald-600 text-xs ml-2">
                          <FiDownload className="w-3 h-3" /> {item.downloads}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {top_performers?.most_copied?.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                    <FiCopy className="w-3 h-3" /> Most Copied Hooks
                  </div>
                  <div className="space-y-2">
                    {top_performers.most_copied.map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-blue-500/5 rounded p-2 text-sm">
                        <span className="text-foreground truncate flex-1">&ldquo;{item.hook}&rdquo;</span>
                        <span className="flex items-center gap-1 text-blue-600 text-xs ml-2">
                          <FiCopy className="w-3 h-3" /> {item.copies}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground mt-3 italic">
                FirstCut learns from your natural workflow - no extra clicks needed.
              </p>
            </div>
          )}

          {/* Pattern Insights */}
          {pattern_insights && Object.keys(pattern_insights).length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <FiAward className="w-4 h-4 text-emerald-500" />
                What Works (From Your Approvals)
              </h4>
              <div className="space-y-3">
                {pattern_insights.preferred_platforms?.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Preferred Platforms</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {pattern_insights.preferred_platforms.map((platform, i) => (
                        <span key={i} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md capitalize">
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {pattern_insights.hook_patterns?.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Hook Patterns</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {pattern_insights.hook_patterns.map((pattern, i) => (
                        <span key={i} className="px-2 py-1 bg-emerald-500/10 text-emerald-600 text-xs rounded-md">
                          &ldquo;{pattern}...&rdquo;
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {pattern_insights.sample_approved_hooks?.length > 0 && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">Sample Approved Hooks</span>
                    <div className="mt-1 space-y-1">
                      {pattern_insights.sample_approved_hooks.map((hook, i) => (
                        <div key={i} className="text-sm text-foreground bg-muted/30 rounded p-2 italic">
                          &ldquo;{hook}&rdquo;
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Avoidance Insights */}
          {avoidance_insights?.avoided_hooks?.length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <FiX className="w-4 h-4 text-red-400" />
                What to Avoid (From Rejections)
              </h4>
              <div className="space-y-1">
                {avoidance_insights.avoided_hooks.map((hook, i) => (
                  <div key={i} className="text-sm text-muted-foreground bg-red-500/5 border border-red-500/20 rounded p-2 line-through opacity-70">
                    &ldquo;{hook}&rdquo;
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* JIT Skill Status */}
          {jit_skill && (
            <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-4 border border-primary/10">
              <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <FiZap className="w-4 h-4 text-amber-500" />
                JIT Industry Playbook
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Industry</span>
                  <div className="font-medium text-foreground capitalize">{jit_skill.industry || 'General'}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Version</span>
                  <div className="font-medium text-foreground">v{jit_skill.refinement_version || 1}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Learned From</span>
                  <div className="font-medium text-foreground">{jit_skill.approval_count || 0} approvals</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Last Refined</span>
                  <div className="font-medium text-foreground">
                    {jit_skill.last_refinement 
                      ? new Date(jit_skill.last_refinement).toLocaleDateString()
                      : 'Initial'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cold Start Help */}
          {isColdStart && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <h4 className="font-medium text-amber-600 mb-2">Getting Started with AI Learning</h4>
              <p className="text-sm text-muted-foreground mb-3">
                FirstCut will start learning your preferences once you approve or reject content. 
                Here&apos;s how to train it:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-start gap-2">
                  <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Approve</strong> content that matches your client&apos;s brand voice and style</span>
                </li>
                <li className="flex items-start gap-2">
                  <FiX className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong>Reject</strong> content that feels off-brand or generic</span>
                </li>
                <li className="flex items-start gap-2">
                  <FiRefreshCw className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>After 3+ approvals, the AI starts using learned patterns</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AILearningInsights;
